import React, { useState } from 'react';
import TrainingDetail from "./TrainingDetail.jsx";

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = String(d.getUTCFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const PlanMyTraining = ({data}) => {
  const [expandedVariation, setExpandedVariation] = useState(null);
  const [addedVariations, setAddedVariations] = useState({});
  const [emailValue, setEmailValue] = useState('');
  const [expandedNotify, setExpandedNotify] = useState(null);
  const [notifyStatus, setNotifyStatus] = useState({});

  const toggleNotify = (centerId) => {
    setExpandedNotify(prev => prev === centerId ? null : centerId);
    setNotifyStatus(prev => ({ ...prev, [centerId]: null }));
    setEmailValue('');
  };

  const handleNotifySubmit = (centerId, e) => {
    e.preventDefault();

    if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setNotifyStatus(prev => ({ ...prev, [centerId]: { type: 'error', message: Drupal.t('Email invalide.') } }));
      return;
    }

    setNotifyStatus(prev => ({ ...prev, [centerId]: { type: 'loading' } }));

    fetch('/product/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailValue, product: data.productId }),
    })
      .then(response => response.json().then(body => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          throw new Error(body.error || Drupal.t("Erreur lors de l'enregistrement."));
        }
        setNotifyStatus(prev => ({ ...prev, [centerId]: { type: 'success', message: Drupal.t("À partir de maintenant, vous serez averti(e) dès qu'une nouvelle place ou session sera mise en ligne.") } }));
      })
      .catch(error => {
        setNotifyStatus(prev => ({ ...prev, [centerId]: { type: 'error', message: error.message } }));
      });
  };

  const variationsByCenter = {};
  Object.entries(data.variations || {}).forEach(([id, variation]) => {
    const key = `tc_${variation.training_center_id}`;
    if (!variationsByCenter[key]) variationsByCenter[key] = [];
    variationsByCenter[key].push({ id, ...variation });
  });

  // Sort each center's variations by start_date ASC
  Object.values(variationsByCenter).forEach(vars => {
    vars.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
  });

  // Build centers list:
  // - Use allCenters (from product field_training_centers) if available
  // - Otherwise derive from variations grouped by training_center_id
  const centers = data.allCenters && data.allCenters.length > 0
    ? [...data.allCenters]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(c => ({ ...c, key: `tc_${c.id}` }))
    : Object.entries(variationsByCenter)
        .map(([key, vars]) => ({
          id: key,
          key,
          name: vars[0] ? (vars[0].place_name || key) : key,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

  const getPlaceDataForVariation = (variationId) => {
    const placeName = data.center
      ? Object.entries(data.center).find(([key]) => {
          // center keys may be merged like "123_456", check if variationId is in the key
          return key === variationId || key.split('_').includes(String(variationId));
        })?.[1]
      : null;

    if (!placeName) return null;
    const placeId = data.centerIds ? data.centerIds[placeName] : null;
    if (!placeId) return null;
    return (data.centerData || []).find(c => String(c.id) === String(placeId)) || null;
  };

  const handleAddToCart = (variationId, e) => {
    e.preventDefault();
    const form = document.querySelector('#ifapme-variations-cart-form');
    const fieldVariations = document.querySelector('#form-variations-value');
    if (fieldVariations) fieldVariations.value = variationId;
    if (form) {
      form.submit();
      setAddedVariations(prev => ({ ...prev, [variationId]: true }));
    }
  };

  const toggleDetail = (variationId) => {
    setExpandedVariation(prev => prev === variationId ? null : variationId);
  };

  const renderActionButton = (variation, isAdded) => {
    if (variation.purchase_process === 'contact_form' && variation.contact_url) {
      return (
        <a className="plan-my-training--add-btn" href={variation.contact_url}>
          {Drupal.t("Prendre contact")}
        </a>
      );
    }

    if (variation.purchase_process === 'external_link' && variation.cta_link?.uri) {
      const isExternal = /^https?:\/\//.test(variation.cta_link.uri);
      return (
        <a
          className="plan-my-training--add-btn"
          href={variation.cta_link.uri}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {variation.cta_link.title || Drupal.t("Ajouter")}
        </a>
      );
    }

    return (
      <button
        className={`plan-my-training--add-btn${isAdded ? ' is-added' : ''}`}
        onClick={e => handleAddToCart(variation.id, e)}
        disabled={isAdded}
      >
        {isAdded ? (
          <>
            <i className="icon-check" aria-hidden="true"></i>
            {Drupal.t("Ajouté")}
          </>
        ) : (
          <>+ {Drupal.t("Ajouter")}</>
        )}
      </button>
    );
  };

  const hasAddedAny = Object.keys(addedVariations).length > 0;
  const totalVariations = Object.keys(data.variations || {}).length;

  // Empty state: no sessions and no locations on the product
  if (totalVariations === 0 && (!data.allCenters || data.allCenters.length === 0)) {
    return (
      <div className="plan-my-training">
        <div className="plan-my-training--empty-state">
          <p>{Drupal.t("Aucune session planifiée pour le moment. Laissez-nous votre email pour être prevenu dès l'ouverture des inscriptions.")}</p>
          <div className="plan-my-training--email-form">
            <input
              type="email"
              className={`plan-my-training--email-input${notifyStatus.empty?.type === 'error' ? ' error' : ''}`}
              placeholder={Drupal.t("Email")}
              value={emailValue}
              onChange={e => setEmailValue(e.target.value)}
              aria-invalid={notifyStatus.empty?.type === 'error'}
            />
            <button
              className="plan-my-training--email-submit btn button-cta"
              onClick={e => handleNotifySubmit('empty', e)}
              disabled={notifyStatus.empty?.type === 'loading'}
            >
              {Drupal.t("Rester informé")}
            </button>
          </div>
          {notifyStatus.empty?.type === 'error' && (
            <p className="plan-my-training--notify-error">{notifyStatus.empty.message}</p>
          )}
          {notifyStatus.empty?.type === 'success' && (
            <p>{notifyStatus.empty.message}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="plan-my-training">
      {centers.map(center => {
        const lookupKey = center.key || `tc_${center.id}`;
        const centerVariations = variationsByCenter[lookupKey] || [];

        return (
          <div key={center.id} className="plan-my-training--center">
            <h3 className="plan-my-training--center-title">{center.name}</h3>

            {centerVariations.length === 0 ? (
              <>
                <div className="plan-my-training--no-date">
                  <em>{Drupal.t("Aucune date planifiée")}</em>
                  <a href="#" className="plan-my-training--notify" onClick={e => { e.preventDefault(); toggleNotify(center.id); }}>
                    <i className="icon-bell" aria-hidden="true"></i>
                    {Drupal.t("Me prévenir")}
                  </a>
                </div>

                {expandedNotify === center.id && (
                  <div className="plan-my-training--notify-form">
                    {notifyStatus[center.id]?.type === 'success' ? (
                      <p>{notifyStatus[center.id].message}</p>
                    ) : (
                      <>
                        <p>{Drupal.t("Laissez-nous votre email, on vous écrit dès qu'une date est planifiée à @center.", { '@center': center.name })}</p>
                        <div className="plan-my-training--email-form">
                          <input
                            type="email"
                            className={`plan-my-training--email-input${notifyStatus[center.id]?.type === 'error' ? ' error' : ''}`}
                            placeholder={Drupal.t("Email")}
                            value={emailValue}
                            onChange={e => setEmailValue(e.target.value)}
                            aria-invalid={notifyStatus[center.id]?.type === 'error'}
                          />
                          <button
                            className="plan-my-training--email-submit btn button-cta"
                            onClick={e => handleNotifySubmit(center.id, e)}
                            disabled={notifyStatus[center.id]?.type === 'loading'}
                          >
                            {Drupal.t("Confirmer")}
                          </button>
                        </div>
                        {notifyStatus[center.id]?.type === 'error' && (
                          <p className="plan-my-training--notify-error">{notifyStatus[center.id].message}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <ul className="plan-my-training--dates">
                {centerVariations.map(variation => {
                  const isExpanded = expandedVariation === variation.id;
                  const isAdded = addedVariations[variation.id];
                  const placeData = getPlaceDataForVariation(variation.id);

                  const startFormatted = formatDate(variation.start_date);
                  const endFormatted = formatDate(variation.end_date);
                  const dateLabel = variation.label || (startFormatted
                    ? (endFormatted ? `${startFormatted} - ${endFormatted}` : startFormatted)
                    : '');

                  return (
                    <li key={variation.id} className={`plan-my-training--date-row${isExpanded ? ' is-expanded' : ''}`}>
                      <div className="plan-my-training--date-header">
                        <button
                          className="plan-my-training--expand"
                          onClick={() => toggleDetail(variation.id)}
                          aria-expanded={isExpanded}
                          aria-controls={`training-detail-${variation.id}`}
                        >
                          <i className={`icon-angle-${isExpanded ? 'down' : 'right'}`} aria-hidden="true"></i>
                          <i className="icon-calendar" aria-hidden="true"></i>
                          <span>{dateLabel}</span>
                        </button>

                        <button
                          type="button"
                          className="plan-my-training--details-link"
                          onClick={() => toggleDetail(variation.id)}
                          aria-expanded={isExpanded}
                          aria-controls={`training-detail-${variation.id}`}
                        >
                          {isExpanded ? Drupal.t("Masquer les détails") : Drupal.t("Voir les détails")}
                        </button>

                        <div className="plan-my-training--date-actions">
                          {renderActionButton(variation, isAdded)}
                        </div>
                      </div>

                      {isExpanded && (
                        <div
                          className="plan-my-training--detail"
                          id={`training-detail-${variation.id}`}
                          role="region"
                        >
                          <TrainingDetail
                            variation={variation}
                            placeData={placeData}
                            trainingCenter={data.trainingCenter}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}

      <div className="plan-my-training--footer">
        <div className="plan-my-training--no-slot">
          <i className="icon-bell" aria-hidden="true"></i>
          <span>{Drupal.t("Aucune date ne vous convient ?")}</span>
          <a href="#" className="plan-my-training--stay-informed" onClick={e => { e.preventDefault(); toggleNotify('footer'); }}>
            {Drupal.t("Rester informé")}
            <i className="icon-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
        {hasAddedAny && (
          <a href="/panier" className="plan-my-training--cart-btn btn button-cta">
            {Drupal.t("Voir mon panier")}
          </a>
        )}
      </div>

      {expandedNotify === 'footer' && (
        <div className="plan-my-training--notify-form">
          {notifyStatus.footer?.type === 'success' ? (
            <p>{notifyStatus.footer.message}</p>
          ) : (
            <>
              <p>{Drupal.t("Laissez-nous votre email, on vous écrit dès qu'une nouvelle date est planifiée.")}</p>
              <div className="plan-my-training--email-form">
                <input
                  type="email"
                  className={`plan-my-training--email-input${notifyStatus.footer?.type === 'error' ? ' error' : ''}`}
                  placeholder={Drupal.t("Email")}
                  value={emailValue}
                  onChange={e => setEmailValue(e.target.value)}
                  aria-invalid={notifyStatus.footer?.type === 'error'}
                />
                <button
                  className="plan-my-training--email-submit btn button-cta"
                  onClick={e => handleNotifySubmit('footer', e)}
                  disabled={notifyStatus.footer?.type === 'loading'}
                >
                  {Drupal.t("Confirmer")}
                </button>
              </div>
              {notifyStatus.footer?.type === 'error' && (
                <p className="plan-my-training--notify-error">{notifyStatus.footer.message}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanMyTraining;
